import 'katex/dist/katex.min.css';
import React from 'react';
import { BlockMath } from 'react-katex';
import type { components } from '../../../types/api-generated';

// Use backend-generated types
type ModelBlock = components["schemas"]["ModelNode-Output"]["model"];

interface EquationModalProps {
  selectedModel: {
    name: string;
    model: ModelBlock;
  };
  onClose: () => void;
}

const EquationModal: React.FC<EquationModalProps> = ({ selectedModel, onClose }) => {
  // Type guard to check if model is mechanistic (has equations property)
  const isMechanisticModel = (model: ModelBlock): model is components["schemas"]["ModelBlockMechanistic-Output"] => {
    return 'equations' in model;
  };

  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[80vh] overflow-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">{selectedModel.name} - Equations</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>
        
        <div className="space-y-4">
          {selectedModel.model.category && (
            <div>
              <h4 className="font-semibold mb-2">Model Type:</h4>
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                {selectedModel.model.category}
              </span>
            </div>
          )}

          {isMechanisticModel(selectedModel.model) && selectedModel.model.equations && (
            <div>
              <h4 className="font-semibold mb-2">Equations:</h4>
              <div className="bg-gray-50 p-4 rounded border">
                <BlockMath math={selectedModel.model.equations} />
              </div>
            </div>
          )}

          {selectedModel.model.input_vars && selectedModel.model.input_vars.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Input Variables:</h4>
              <div className="grid grid-cols-1 gap-2">
                {selectedModel.model.input_vars.map((variable, index) => (
                  <div key={index} className="bg-blue-50 p-2 rounded text-sm">
                    <span className="font-mono">{variable.standard_name}</span>
                    <span className="text-gray-600 ml-2">({variable.units})</span>
                    <div className="text-xs text-gray-500">{variable.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedModel.model.output_vars && selectedModel.model.output_vars.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Output Variables:</h4>
              <div className="grid grid-cols-1 gap-2">
                {selectedModel.model.output_vars.map((variable, index) => (
                  <div key={index} className="bg-green-50 p-2 rounded text-sm">
                    <span className="font-mono">{variable.standard_name}</span>
                    <span className="text-gray-600 ml-2">({variable.units})</span>
                    <div className="text-xs text-gray-500">{variable.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EquationModal;
